import {
    ProcedureArgsArray,
    ProcedureName,
    ULPath,
    SocialLoginConfig,
    SocialSdkConfig,
    ProcedureArgs,
    ULRpcRequestMessage,
    ULRpcResponseMessage,
    ULRpcResponseErrorMessage,
    UserPreference,
    LoginSuccessAdditional,
    PasskeyRegistrationExitState,
} from '@ul/common';
import { BaseRpcCaller } from './BaseRpcCaller';
import { WithULRpcCaller } from './WithULRpcCaller';

class PostMessageRpcCaller<A extends ProcedureArgs = ProcedureArgsArray> extends BaseRpcCaller<A> {
    constructor(
        rpcId: string,
        target: WindowProxy,
        receiver: Window = window,
        parentWhitelistedOrigin: string = '',
        useExactUrl: boolean = false
    ) {
        super(rpcId);
        this.target = target;
        this.receiver = receiver;
        this.parentWhitelistedOrigin = parentWhitelistedOrigin;
        this.useExactUrl = useExactUrl;
    }

    private target: WindowProxy;
    private receiver: Window;
    private parentWhitelistedOrigin: string;
    private useExactUrl: boolean;

    protected sendRequest = <P extends ProcedureName>(request: ULRpcRequestMessage<P, A>): boolean => {
        if (this.parentWhitelistedOrigin) {
            console.log('sendrequest', request.procedureName, this.getUrlWithWWW(this.parentWhitelistedOrigin));
            this.target.postMessage(request, this.getUrlWithWWW(this.parentWhitelistedOrigin));
        } else {
            const origin = this.target.origin ?? this.target.location.origin;
            this.target.postMessage(request, origin);
        }
        return true;
    };

    private getUrlWithWWW = (originString: string) => {
        if (originString.includes('www.') || this.useExactUrl) return originString;
        else {
            const url = new URL(originString);
            return url.protocol + '//www.' + url.hostname;
        }
    };

    protected receiveResponse = <P extends ProcedureName>(
        request: ULRpcRequestMessage<P, A>,
        resolve: (value: ULRpcResponseErrorMessage | ULRpcResponseMessage<P>) => void
    ) => {
        const listener = (event: MessageEvent) => {
            const { data } = event;
            const response = data as ULRpcResponseMessage<P> | ULRpcResponseErrorMessage;
            const origin = this.parentWhitelistedOrigin
                ? this.getUrlWithWWW(this.parentWhitelistedOrigin)
                : this.target.origin ?? this.target.location.origin;
            if (
                request.procedureName !== response.procedureName ||
                request.nonce !== response.nonce ||
                request.appId !== response.appId ||
                event.origin !== origin
            ) {
                return;
            }
            if (this.parentWhitelistedOrigin) console.log('receiveResponse', origin, this.parentWhitelistedOrigin);
            resolve(response);
        };

        this.receiver.addEventListener('message', listener);
        return () => this.receiver.removeEventListener('message', listener);
    };
}

export class PostMessageULRpcCaller extends WithULRpcCaller(PostMessageRpcCaller) {
    /**********************************
     ****** UL RPC caller methods *****
     **********************************/

    public onExit = () => this.call('onExit', []);

    public onLoginComplete = (isSuccess: boolean, userPreference?: UserPreference) =>
        this.call('onLoginComplete', [isSuccess, userPreference]);

    public onLoginSuccess = (additional?: LoginSuccessAdditional) =>
        this.callAsync('onLoginSuccess', [additional], 60000);

    public onNotificationPush = (message: string, isSuccess: boolean) =>
        this.call('onNotificationPush', [message, isSuccess]);

    public onRouteChange = (path: ULPath, isBack: boolean) => this.call('onRouteChange', [path, isBack]);

    public onSignout = () => this.call('onSignout', []);

    public onSocialLogin = (config: SocialLoginConfig) => this.callAsync('onSocialLogin', [config], 1800000);

    public onSocialSdkLoad = (config: SocialSdkConfig) => this.callAsync('onSocialSdkLoad', [config], 300000);

    public onSpaLoad = (spaLoadCompletedAt: number) => this.call('onSpaLoad', [spaLoadCompletedAt]);

    public onWindowSizeChanged = (height: number, width: number) => this.call('onWindowSizeChanged', [height, width]);

    public onDeleteAccountSuccess = () => this.call('onDeleteAccountSuccess', []);

    public onDeleteAccountExit = () => this.call('onDeleteAccountExit', []);

    public onForceLogoutSuccess = () => this.call('onForceLogoutSuccess', []);

    public onForceLogoutExit = () => this.call('onForceLogoutExit', []);

    public onDismissButtonClick = () => this.call('onDismissButtonClick', []);

    public onVerifyOtp = (message: string, isSuccess: boolean, status: number) =>
        this.call('onVerifyOtp', [message, isSuccess, status]);

    public onWalletAuthenticated = (success: boolean) => this.call('onWalletAuthenticated', [success]);

    public onTransactionWillLoad = async () => this.callAsync('onTransactionWillLoad', [], 25000);

    public onPasskeyRegister = async (challengeOptions: any) =>
        this.callAsync('onPasskeyRegister', [challengeOptions], 180000);

    public onPasskeyAuthenticate = async (challengeOptions: any) =>
        this.callAsync('onPasskeyAuthenticate', [challengeOptions], 180000);

    public onPasskeySupportsAutofill = async () => this.callAsync('onPasskeySupportsAutofill', [], 5000);

    public onPasskeyRegistrationExit = (exitState: PasskeyRegistrationExitState) =>
        this.call('onPasskeyRegistrationExit', [exitState]);

    public onPasskeyDelete = (rpId: string, credentialId: string) => {
        this.call('onPasskeyDelete', [rpId, credentialId]);
    };
}
